import "./Meets.css";
import { type MouseEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { AuthError } from "../../api/client.js";
import { getGroups } from "../../builder/api.js";
import { Dialog, Icon } from "../../builder/components/ui.js";
import { SAVE_DELAY } from "../../builder/hooks/useDraftDoc.js";
import { type SavedGroup, shortNames, type TeamAthlete } from "../../builder/model/groups.js";
import { type Column, PageHeader, Table } from "../../kit/index.js";
import { usePortal, useTitle } from "../context.js";
import { addMeet, changeMeet, deleteMeet, errorMessage, groupsKey, loadMeets, meetsKey, useResource } from "../data.js";
import { addDays, dayLong, parseDay, todayString } from "../model/days.js";
import {
  addMonths,
  comingUp,
  daysAway,
  goingNames,
  goingText,
  inDays,
  MEET_TEXT_MAX,
  type MeetFields,
  meetBody,
  meetProblems,
  meetsOn,
  meetWhen,
  meetWhenLong,
  monthGrid,
  namesLine,
  monthOf,
  monthTitle,
  withWrites,
} from "../model/meets.js";
import type { Meet, MeetChange } from "../model/types.js";

/** `flag.checkered`, drawn: a pole and a checked flag. */
export function FlagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M2 1.5h1.4V15H2zM3.4 2h2.65v2.4H3.4zM8.7 2h2.65v2.4H8.7zM6.05 4.4h2.65v2.4H6.05zM11.35 4.4H14v2.4h-2.65zM3.4 6.8h2.65v2.4H3.4zM8.7 6.8h2.65v2.4H8.7z" />
      <path fill="none" stroke="currentColor" strokeWidth="0.9" d="M3.4 2.45h10.15v6.3H3.4" />
    </svg>
  );
}

/** The team's meets overlapping `from`..`until`; what was last read while they are read again, none when they can't be. */
export function useMeets(from: string | null, until: string | null): Meet[] | undefined {
  const key = from && until ? meetsKey(from, until) : null;
  const read = useResource(key, () => loadMeets(from!, until!));
  const kept = useRef<Meet[] | undefined>(undefined);
  if (read.value) kept.current = read.value;
  return read.value ?? (read.error ? [] : kept.current);
}

/** Groups are a convenience here: any failure but signing out is none (as the builder reads them). */
export const readGroups = () =>
  getGroups().catch((error: unknown) => {
    if (error instanceof AuthError) throw error;
    return null;
  });

/** The team's athletes and saved groups; for a coach who can't read groups, the athletes of this week. */
export function useTeamPeople(): { athletes: TeamAthlete[]; groups: SavedGroup[] } {
  const { model } = usePortal();
  const data = useResource(groupsKey, readGroups).value;
  return useMemo(
    () => (data ? { athletes: data.athletes, groups: data.groups } : { athletes: (model?.athletes ?? []).map((a) => ({ id: a.id, name: a.name })), groups: [] }),
    [data, model],
  );
}

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const meetPath = (id: string) => "/meet/" + encodeURIComponent(id);

interface Adding {
  key: number;
  date: string;
  /** Set once the server has it. */
  id: string | null;
}

/** Meets (DESIGN §27.3): a month's calendar with Coming up beside it, and a meet in a dialog at its own address. */
export function Meets() {
  const { me } = usePortal();
  const { id } = useParams();
  const navigate = useNavigate();
  const canPlan = !!me.person.canPlan;
  const today = todayString();
  const [month, setMonth] = useState(() => monthOf(today));
  const weeks = useMemo(() => monthGrid(month), [month]);
  const from = weeks[0]![0]!;
  const until = weeks[weeks.length - 1]![6]!;
  const yearOn = addDays(today, 365);
  const [writes, setWrites] = useState<ReadonlyMap<string, Meet | null>>(() => new Map());
  const monthRead = useMeets(from, until);
  const nextRead = useMeets(today, yearOn);
  const shown = useMemo(() => withWrites(monthRead ?? [], writes, from, until), [monthRead, writes, from, until]);
  const next = useMemo(() => comingUp(withWrites(nextRead ?? [], writes, today, yearOn), today), [nextRead, writes, today, yearOn]);
  const people = useTeamPeople();
  const short = useMemo(() => shortNames(people.athletes), [people.athletes]);
  const [adding, setAdding] = useState<Adding | null>(null);
  const seq = useRef(0);
  const opener = useRef<HTMLElement | null>(null);

  const open = id ? (writes.has(id) ? writes.get(id) : (shown.find((m) => m.id === id) ?? next.find((m) => m.id === id))) : undefined;
  const creating = adding && (!id || adding.id === id) ? adding : null;
  const editorOpen = !!creating || !!id;
  useTitle(open ? open.name + " · Meets" : creating ? "New meet · Meets" : "Meets");

  const remember = () => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  };
  const add = (date: string) => {
    if (!canPlan) return;
    remember();
    setAdding({ key: ++seq.current, date, id: null });
  };
  const wrote = useCallback((m: Meet) => setWrites((w) => new Map(w).set(m.id, m)), []);
  const deleted = useCallback((mid: string) => setWrites((w) => new Map(w).set(mid, null)), []);
  const created = useCallback(
    (m: Meet, stillOpen: boolean) => {
      wrote(m);
      if (!stillOpen) return;
      setAdding((a) => (a ? { ...a, id: m.id } : a));
      navigate(meetPath(m.id));
    },
    [wrote, navigate],
  );
  const close = useCallback(() => {
    setAdding(null);
    if (id) navigate("/meets");
  }, [id, navigate]);

  const wasOpen = useRef(editorOpen);
  useEffect(() => {
    if (wasOpen.current && !editorOpen && opener.current?.isConnected) opener.current.focus();
    wasOpen.current = editorOpen;
  }, [editorOpen]);

  const columns: Column<Meet>[] = [
    {
      key: "name",
      label: "Meet",
      wrap: true,
      render: (m) => (
        <span className="mt-next-name">
          {m.name}
          <span className="k-sub">{[meetWhen(m), inDays(daysAway(m, today)), m.place].filter(Boolean).join(" · ")}</span>
          <GoingLine meet={m} athletes={people.athletes} short={short} />
        </span>
      ),
    },
    { key: "going", label: "Going", align: "end", render: (m) => <span className="num">{m.people.length}</span> },
  ];

  const nextOne = next[0];
  const context = next.length
    ? (next.length === 1 ? "1 meet" : next.length + " meets") + " coming up · next " + nextOne!.name + " " + inDays(daysAway(nextOne!, today))
    : "No meets coming up";

  return (
    <div className="k-page">
      <PageHeader
        title="Meets"
        context={context}
        actions={
          canPlan ? (
            <button type="button" className="btn" onClick={() => add(month === monthOf(today) ? today : month)}>
              {Icon.plus}
              <span>Add meet</span>
            </button>
          ) : null
        }
      />
      <div className="mt-layout">
        <section className="mt-cal" aria-labelledby="mt-month">
          <div className="mt-cal-head">
            <h2 id="mt-month">{monthTitle(month)}</h2>
            <div className="bp-week-nav" role="group" aria-label="Month">
              <button type="button" className="bp-icon-btn" aria-label="Previous month" title="Previous month" onClick={() => setMonth((m) => addMonths(m, -1))}>
                {Icon.chevronLeft}
              </button>
              <button type="button" className="bp-icon-btn" aria-label="Next month" title="Next month" onClick={() => setMonth((m) => addMonths(m, 1))}>
                {Icon.chevron}
              </button>
              <button type="button" className="btn bp-small" disabled={month === monthOf(today)} onClick={() => setMonth(monthOf(today))}>
                Today
              </button>
            </div>
          </div>
          <table className="mt-grid" aria-labelledby="mt-month">
            <thead>
              <tr>
                {WEEKDAYS.map((d) => (
                  <th key={d} scope="col">
                    <span aria-hidden="true">{d.slice(0, 3)}</span>
                    <span className="sr">{d}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week) => (
                <tr key={week[0]}>
                  {week.map((day) => (
                    <CalendarDay key={day} day={day} month={month} today={today} meets={meetsOn(shown, day)} athletes={people.athletes} selected={id} canPlan={canPlan} onAdd={add} onOpen={remember} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <section className="mt-next" aria-labelledby="mt-next-h">
          <h2 id="mt-next-h">Coming up</h2>
          <Table
            rows={next}
            columns={columns}
            rowKey={(m) => m.id}
            href={(m) => meetPath(m.id)}
            selected={id ?? null}
            label="Coming up"
            empty={nextRead === undefined ? "Loading…" : canPlan ? "No meets coming up. Click a day on the calendar to add one." : "No meets coming up."}
          />
        </section>
      </div>
      {creating ? (
        <MeetEditor key={"new" + creating.key} initial={creating.id ? (open ?? null) : null} date={creating.date} people={people} onCreated={created} onSaved={wrote} onDeleted={deleted} onClose={close} />
      ) : id && open ? (
        canPlan ? (
          <MeetEditor key={"m" + id} initial={open} date={open.date} people={people} onCreated={created} onSaved={wrote} onDeleted={deleted} onClose={close} />
        ) : (
          <MeetView meet={open} athletes={people.athletes} onClose={close} />
        )
      ) : id ? (
        <Dialog open onClose={close} title="Meet">
          <p className="k-muted" role="status">
            {monthRead === undefined || nextRead === undefined ? "Loading the meet…" : "This meet isn't on the calendar any more."}
          </p>
        </Dialog>
      ) : null}
    </div>
  );
}

/** Under a Coming up meet, who is going by name (§27.3.1). */
function GoingLine({ meet, athletes, short }: { meet: Meet; athletes: TeamAthlete[]; short: Map<string, string> }) {
  const going = new Set(meet.people);
  const names = athletes.filter((a) => going.has(a.id)).map((a) => short.get(a.id) ?? a.name);
  if (!names.length) return null;
  return (
    <span className="mt-next-who">
      <span className="sr">Going: </span>
      {namesLine(names)}
    </span>
  );
}

function CalendarDay({
  day,
  month,
  today,
  meets,
  athletes,
  selected,
  canPlan,
  onAdd,
  onOpen,
}: {
  day: string;
  month: string;
  today: string;
  meets: Meet[];
  athletes: TeamAthlete[];
  selected: string | undefined;
  canPlan: boolean;
  onAdd: (day: string) => void;
  onOpen: () => void;
}) {
  const uid = useId();
  const [tip, setTip] = useState<string | null>(null);
  const isToday = day === today;
  const outside = monthOf(day) !== month;
  const n = parseDay(day).getDate();
  const click = (e: MouseEvent<HTMLTableCellElement>) => {
    if ((e.target as Element).closest("a, button")) return;
    onAdd(day);
  };
  return (
    <td className={"mt-day" + (outside ? " is-outside" : "") + (isToday ? " is-today" : "") + (canPlan ? " is-addable" : "")} onClick={canPlan ? click : undefined}>
      <div className="mt-day-head">
        {canPlan ? (
          <button type="button" className="mt-day-num num" aria-label={`Add a meet on ${dayLong(day)}${isToday ? ", today" : ""}`} title="Add a meet" onClick={() => onAdd(day)}>
            {n}
          </button>
        ) : (
          <span className="mt-day-num num">
            <span aria-hidden="true">{n}</span>
            <span className="sr">{dayLong(day)}</span>
          </span>
        )}
        {isToday ? <span className="mt-today">Today</span> : null}
      </div>
      {meets.length ? (
        <ul className="mt-chips">
          {meets.map((m, i) => {
            const names = goingNames(m, athletes);
            const tipID = `${uid}-t${i}`;
            return (
              <li key={m.id} className="mt-chip-item" onMouseEnter={() => setTip(m.id)} onMouseLeave={() => setTip(null)}>
                <Link
                  className="mt-chip"
                  to={meetPath(m.id)}
                  aria-current={selected === m.id ? "true" : undefined}
                  aria-describedby={tipID}
                  onClick={onOpen}
                  onFocus={() => setTip(m.id)}
                  onBlur={() => setTip(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && tip === m.id) {
                      e.preventDefault();
                      setTip(null);
                    }
                  }}
                >
                  <span className="mt-chip-name">{m.name}</span>
                  <span className="num" aria-hidden="true">
                    {" · "}
                    {m.people.length}
                  </span>
                  <span className="sr">, {goingText(m.people.length)}</span>
                </Link>
                <span id={tipID} className="mt-tip" role="tooltip" hidden={tip !== m.id}>
                  {m.place ? <span className="mt-tip-place">{m.place}. </span> : null}
                  {names.length ? "Going: " + names.join(", ") : m.people.length ? goingText(m.people.length) : "Nobody going yet"}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </td>
  );
}

type Status = { kind: "idle" } | { kind: "saving" } | { kind: "saved" } | { kind: "error"; message: string };

const fieldsOf = (m: Meet | null, date: string): MeetFields => ({ name: m?.name ?? "", date: m?.date ?? date, endDate: m?.endDate ?? "", place: m?.place ?? "" });

const sameSet = (a: readonly string[], b: readonly string[]) => a.length === b.length && a.every((x) => b.includes(x));

/**
 * A meet as he writes it, saved as he goes (as the builder autosaves, after a pause): the first save
 * with a name adds it, every one after changes it. Closing saves what is waiting.
 */
function MeetEditor({
  initial,
  date,
  people,
  onCreated,
  onSaved,
  onDeleted,
  onClose,
}: {
  initial: Meet | null;
  date: string;
  people: { athletes: TeamAthlete[]; groups: SavedGroup[] };
  onCreated: (m: Meet, stillOpen: boolean) => void;
  onSaved: (m: Meet) => void;
  onDeleted: (id: string) => void;
  onClose: () => void;
}) {
  const { announce } = usePortal();
  const uid = useId();
  const [fields, setFields] = useState<MeetFields>(() => fieldsOf(initial, date));
  const [going, setGoing] = useState<string[]>(() => initial?.people ?? []);
  const [touched, setTouched] = useState<Set<keyof MeetFields>>(() => new Set());
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [groupID, setGroupID] = useState("");
  const [meetID, setMeetID] = useState<string | null>(initial?.id ?? null);

  const latest = useRef({ fields, going });
  const saved = useRef<{ body: ReturnType<typeof meetBody>; people: string[] } | null>(initial ? { body: meetBody(fieldsOf(initial, date)), people: initial.people } : null);
  const idRef = useRef<string | null>(initial?.id ?? null);
  const running = useRef(false);
  const again = useRef(false);
  const waiting = useRef(false);
  const closed = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const handlers = useRef({ onCreated, onSaved });
  handlers.current = { onCreated, onSaved };

  const saveNow = useCallback(async () => {
    clearTimeout(timer.current);
    if (running.current) {
      again.current = true;
      return;
    }
    waiting.current = false;
    const now = latest.current;
    if (Object.keys(meetProblems(now.fields)).length) return;
    const body = meetBody(now.fields);
    running.current = true;
    try {
      let meet: Meet | null = null;
      if (!idRef.current) {
        setStatus({ kind: "saving" });
        meet = await addMeet(body);
        idRef.current = meet.id;
        saved.current = { body, people: meet.people ?? [] };
        setMeetID(meet.id);
        handlers.current.onCreated(meet, !closed.current);
      }
      const was = saved.current!;
      const change: MeetChange = {};
      if (body.name !== was.body.name) change.name = body.name;
      if (body.date !== was.body.date) change.date = body.date;
      if (body.endDate !== was.body.endDate) change.endDate = body.endDate;
      if (body.place !== was.body.place) change.place = body.place;
      if (!sameSet(now.going, was.people)) change.people = now.going;
      if (Object.keys(change).length) {
        setStatus({ kind: "saving" });
        meet = await changeMeet(idRef.current!, change);
        saved.current = { body, people: now.going };
      }
      if (meet) handlers.current.onSaved(meet);
      setStatus(meet || saved.current ? { kind: "saved" } : { kind: "idle" });
    } catch (error) {
      if (error instanceof AuthError) return;
      setStatus({ kind: "error", message: errorMessage(error) });
    } finally {
      running.current = false;
      if (again.current) {
        again.current = false;
        void saveNow();
      }
    }
  }, []);

  useEffect(() => {
    closed.current = false;
    return () => {
      closed.current = true;
      if (waiting.current) void saveNow();
    };
  }, [saveNow]);

  const schedule = () => {
    waiting.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void saveNow(), SAVE_DELAY);
  };

  const edit = (change: Partial<MeetFields>) => {
    const next = { ...latest.current.fields, ...change };
    latest.current = { ...latest.current, fields: next };
    setFields(next);
    setTouched((t) => new Set([...t, ...(Object.keys(change) as (keyof MeetFields)[])]));
    schedule();
  };
  const setPeople = (list: string[]) => {
    latest.current = { ...latest.current, going: list };
    setGoing(list);
    schedule();
  };

  const close = () => {
    closed.current = true;
    if (waiting.current) void saveNow();
    onClose();
  };

  const remove = async () => {
    if (!idRef.current) return;
    setDeleting(true);
    clearTimeout(timer.current);
    waiting.current = false;
    try {
      await deleteMeet(idRef.current);
      announce(`Deleted ${latest.current.fields.name.trim() || "the meet"}`);
      closed.current = true;
      onDeleted(idRef.current);
      onClose();
    } catch (error) {
      setDeleting(false);
      if (!(error instanceof AuthError)) setStatus({ kind: "error", message: errorMessage(error) });
    }
  };

  const problems = meetProblems(fields);
  const shownProblem = (k: keyof MeetFields) => (touched.has(k) || (k !== "name" && !!saved.current) ? problems[k] : undefined);
  const group = people.groups.find((g) => g.id === groupID);
  const listed = group ? people.athletes.filter((a) => group.members.includes(a.id)) : people.athletes;
  const chosen = new Set(going);
  const goingCount = people.athletes.filter((a) => chosen.has(a.id)).length;

  const selectGroup = () => {
    if (!group) return;
    const add = group.members.filter((m) => !chosen.has(m) && people.athletes.some((a) => a.id === m));
    if (add.length) setPeople([...going, ...add]);
    announce(add.length ? `Ticked ${add.length === 1 ? "1 athlete" : add.length + " athletes"} of ${group.name}` : `Everyone in ${group.name} is already going`);
  };

  const field = (k: keyof MeetFields) => {
    const p = shownProblem(k);
    return { "aria-invalid": p ? true : undefined, "aria-describedby": p ? `${uid}-${k}-e` : undefined };
  };
  const problem = (k: keyof MeetFields) => {
    const p = shownProblem(k);
    return p ? (
      <span className="bp-field-error" id={`${uid}-${k}-e`}>
        {Icon.warning} {p}
      </span>
    ) : null;
  };

  const footer = (
    <div className="mt-foot">
      <SaveState status={status} unsaved={!meetID} onRetry={() => void saveNow()} />
      {meetID ? (
        confirming ? (
          <span className="mt-confirm" role="group" aria-label="Delete this meet?">
            <span>Delete this meet for everyone?</span>
            <button type="button" className="btn bp-small" onClick={() => setConfirming(false)} autoFocus>
              Cancel
            </button>
            <button type="button" className="btn bp-small bp-destructive" disabled={deleting} onClick={() => void remove()}>
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </span>
        ) : (
          <button type="button" className="btn bp-small bp-destructive" onClick={() => setConfirming(true)}>
            {Icon.trash}
            <span>Delete meet</span>
          </button>
        )
      ) : null}
    </div>
  );

  return (
    <Dialog open onClose={close} title={fields.name.trim() || "New meet"} className="mt-dialog" footer={footer}>
      <form className="mt-form" onSubmit={(e) => e.preventDefault()} aria-label="Meet">
        <label className="bp-field">
          <span>Name</span>
          <input
            data-autofocus={!initial || undefined}
            value={fields.name}
            maxLength={MEET_TEXT_MAX}
            required
            placeholder="Conference champs"
            onChange={(e) => edit({ name: e.target.value })}
            {...field("name")}
          />
        </label>
        {problem("name")}
        <div className="mt-dates">
          <div className="mt-field">
            <label className="bp-field">
              <span>Date</span>
              <input type="date" value={fields.date} required onChange={(e) => edit({ date: e.target.value })} {...field("date")} />
            </label>
            {problem("date")}
          </div>
          <div className="mt-field">
            <label className="bp-field">
              <span>
                Ends <span className="mt-optional">(optional)</span>
              </span>
              <input type="date" value={fields.endDate} min={fields.date || undefined} onChange={(e) => edit({ endDate: e.target.value })} {...field("endDate")} />
            </label>
            {problem("endDate")}
          </div>
        </div>
        <label className="bp-field">
          <span>
            Place <span className="mt-optional">(optional)</span>
          </span>
          <input value={fields.place} maxLength={MEET_TEXT_MAX} placeholder="Where it is" onChange={(e) => edit({ place: e.target.value })} {...field("place")} />
        </label>
        {problem("place")}
        <fieldset className="mt-going">
          <legend>
            Who's going <span className="mt-count">· {goingText(goingCount)}</span>
          </legend>
          {people.groups.length ? (
            <div className="mt-going-tools">
              <label className="mt-filter">
                <span>Group</span>
                <select value={groupID} onChange={(e) => setGroupID(e.target.value)}>
                  <option value="">Everyone</option>
                  {people.groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </label>
              <button type="button" className="btn bp-small" disabled={!group} onClick={selectGroup}>
                Select group
              </button>
            </div>
          ) : null}
          {people.athletes.length === 0 ? (
            <p className="k-muted">No athletes on the team yet.</p>
          ) : listed.length === 0 ? (
            <p className="k-muted">Nobody in {group?.name ?? "this group"}.</p>
          ) : (
            <ul className="bp-ap-list mt-going-list" aria-label={group ? `Athletes in ${group.name}` : "Athletes"}>
              {listed.map((a) => (
                <li key={a.id}>
                  <label className="bp-ap-row">
                    <input type="checkbox" checked={chosen.has(a.id)} onChange={(e) => setPeople(e.target.checked ? [...going, a.id] : going.filter((x) => x !== a.id))} />
                    <span className="bp-ap-name">{a.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </fieldset>
      </form>
    </Dialog>
  );
}

function SaveState({ status, unsaved, onRetry }: { status: Status; unsaved: boolean; onRetry: () => void }) {
  if (status.kind === "error")
    return (
      <span className="bp-save is-error" role="status">
        {Icon.warning}
        <span>Not saved: {status.message}</span>
        <button type="button" className="btn bp-small" onClick={onRetry}>
          Retry
        </button>
      </span>
    );
  if (status.kind === "saving")
    return (
      <span className="bp-save" role="status">
        {Icon.sync}
        <span>Saving…</span>
      </span>
    );
  if (unsaved)
    return (
      <span className="bp-save mt-hint" role="status">
        <span>Saved once it has a name.</span>
      </span>
    );
  return (
    <span className="bp-save" role="status">
      {status.kind === "saved" ? Icon.check : Icon.cloud}
      <span>{status.kind === "saved" ? "Saved" : "Saved as you edit"}</span>
    </span>
  );
}

/** A meet for a coach who doesn't plan: what it is and who is going. */
function MeetView({ meet, athletes, onClose }: { meet: Meet; athletes: TeamAthlete[]; onClose: () => void }) {
  const names = goingNames(meet, athletes);
  return (
    <Dialog open onClose={onClose} title={meet.name} className="mt-dialog">
      <dl className="mt-facts">
        <dt>Date</dt>
        <dd>
          <span aria-hidden="true">{meetWhen(meet)}</span>
          <span className="sr">{meetWhenLong(meet)}</span>
        </dd>
        {meet.place ? (
          <>
            <dt>Place</dt>
            <dd>{meet.place}</dd>
          </>
        ) : null}
        <dt>Who's going · {goingText(meet.people.length)}</dt>
        <dd>{names.length ? <ul className="mt-names">{names.map((n) => <li key={n}>{n}</li>)}</ul> : <span className="k-muted">Nobody yet</span>}</dd>
      </dl>
    </Dialog>
  );
}
